import { useQuery } from '@apollo/client';
import { FieldBlock, InCardsBlock } from '@coast/schemas';
import { isEmpty, isNil } from 'lodash';
import { ReactElement, useContext, useEffect, useMemo } from 'react';
import { Queries } from '@shared/api';
import { View } from '@shared/components/View';
import { MBFieldBlock } from '@shared/components/blocks/lib/field';
import { MBInCardsRollupCount } from '@shared/components/blocks/lib/inCards/rollups/count';
import { getTypoComponent } from '@shared/components/blocks/utils';
import { MBLoadingIndicator } from '@shared/components/loading';
import { ColorThemeContext } from '@shared/styles';
import { API } from '@shared/types/api';
import { useNavigationFocus } from '@shared/util/navigation';

interface Props {
  block: InCardsBlock;
  onPress?(): void;
  renderLeft?(): ReactElement;
  cardId: string;
}

export function MBInCardsBlock({ block, onPress, renderLeft, cardId }: Props) {
  const colors = useContext(ColorThemeContext);
  const fieldBlock = useMemo(
    (): FieldBlock => ({ ...block, name: '', type: 'field', fieldType: 'text' }),
    [block],
  );
  const focused = useNavigationFocus();

  const TypoComponent = getTypoComponent(block.textStyle);

  const { data, loading, refetch } = useQuery<
    API.ListRelatedCardRollupsQuery,
    API.ListRelatedCardRollupsQueryVariables
  >(Queries.card.listRelatedCardRollups, {
    variables: {
      input: {
        id: cardId,
        type: 'IN',
        statuses: ['ACTIVE'],
      },
    },
    fetchPolicy: 'cache-and-network',
    skip: isNil(cardId),
  });

  useEffect(() => {
    if (focused && !isNil(cardId)) {
      refetch();
    }
  }, [cardId, focused, refetch]);

  if (!loading && isEmpty(data?.listRelatedCardRollups)) {
    return null;
  }

  return (
    <MBFieldBlock
      value={null}
      block={fieldBlock}
      onPress={onPress}
      renderLeft={renderLeft}
      presenter={() => {
        if (!data) {
          return <MBLoadingIndicator />;
        }
        if (!data.listRelatedCardRollups?.length) {
          return (
            <TypoComponent color={colors.foregroundInactive}>{block.placeholder}</TypoComponent>
          );
        }
        return (
          <View paddingVertical={1}>
            {data.listRelatedCardRollups.map(entry => (
              <MBInCardsRollupCount
                entry={entry}
                cardId={cardId}
                channelId={entry.channelId}
                key={entry.channelId}
              />
            ))}
          </View>
        );
      }}
    />
  );
}
